import type { CSSProperties, ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import {
  TableBody,
  TableCell,
  TableContainer,
  TableEmptyState,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TableLayout,
  TableRow,
} from "./TablePrimitives";

export type TableSimpleColumn<T> = {
  id: string;
  header: ReactNode;
  cell: (props: { row: { original: T }; index: number }) => ReactNode;
  headerClassName?: string;
  cellClassName?: string;
};

export type TableSimpleProps<T> = {
  className?: string;
  columns: TableSimpleColumn<T>[];
  data: T[];
  getRowId: (row: T) => string | number;
  onRowClick?: (row: T) => void;
  noResultsText?: string;
  isLoading?: boolean;
  loadingText?: string;
  gridClasses: string;
  gridStyles?: CSSProperties;
  roundedBottom?: boolean;
  roundedTop?: boolean;
};

export function TableSimple<T>({
  className,
  columns,
  data,
  getRowId,
  onRowClick,
  noResultsText = "Your search returned 0 results",
  isLoading = false,
  loadingText = "Loading...",
  gridClasses,
  gridStyles,
  roundedBottom = true,
  roundedTop,
}: TableSimpleProps<T>) {
  return (
    <TableContainer
      className={className}
      roundedBottom={roundedBottom}
      roundedTop={roundedTop}
    >
      <TableLayout style={{ display: "grid" }}>
        <TableHeader
          style={{ display: "grid", position: "sticky", top: 0, zIndex: 1 }}
        >
          <TableHeaderRow
            className={twMerge(gridClasses)}
            style={{ ...gridStyles, display: "grid", width: "100%" }}
          >
            {columns.map((column) => (
              <TableHeaderCell
                key={column.id}
                className={twMerge(column.headerClassName)}
              >
                {column.header}
              </TableHeaderCell>
            ))}
          </TableHeaderRow>
        </TableHeader>
        <TableBody style={{ display: "grid" }}>
          {data.map((row, index) => (
            <TableRow
              key={getRowId(row)}
              className={twMerge(gridClasses)}
              interactive={Boolean(onRowClick)}
              style={{ ...gridStyles, display: "grid", width: "100%" }}
              onClick={(event) => {
                const target = event.target as HTMLElement;
                const isInteractive = target.closest(
                  'button, input, select, textarea, a, [role="button"], [data-interactive="true"]',
                );

                if (!isInteractive) {
                  onRowClick?.(row);
                }
              }}
            >
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  padded={true}
                  className={twMerge(column.cellClassName)}
                >
                  {column.cell({
                    row: { original: row },
                    index,
                  })}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </TableLayout>

      {data.length === 0 ? (
        <TableEmptyState>
          {isLoading ? loadingText : noResultsText}
        </TableEmptyState>
      ) : null}
    </TableContainer>
  );
}
